import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BRAND_NAME, BRAND_HOME_URL, SUPPORT_EMAIL } from './branding';
import BrokenPage from './components/BrokenPage';
import { PolicyLinks } from './components/PolicyLinks';
import { NativeBookingPicker, type BookingBrandPayload } from './components/NativeBookingPicker';
import { fetchWithRetry } from './lib/fetchRetry';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface PersonPublic {
  slug: string;
  name: string;
  role: string | null;
  bio: string | null;
  bio_long: string | null;
  first_name: string | null;
  last_name: string | null;
  booking_url: string | null;
  bookingUrls: Record<string, string>;
  linkedin_url: string | null;
  instagram_url: string | null;
  /** CC-mirrored public links (migration 0234). Null for local-only people. */
  teamPageUrl: string | null;
  websiteUrl: string | null;
  social: Array<{ network: string; url: string }>;
  photoUrl: string | null;
  /** The booking type the BARE /book/:slug serves — null when this person has
   *  no generic page yet, or theirs is still inactive. */
  defaultBookingKey: string | null;
}

/** Sentence-case label for a social network key. The keys come from CC's feed,
 *  so this deliberately falls through to a capitalised key for a network CC
 *  adds later rather than dropping the link. */
const SOCIAL_LABELS: Record<string, string> = {
  linkedin: 'LinkedIn', youtube: 'YouTube', instagram: 'Instagram',
  facebook: 'Facebook', twitter: 'X',
};
const socialLabel = (n: string) => SOCIAL_LABELS[n] ?? (n.charAt(0).toUpperCase() + n.slice(1));

export default function BookingPage() {
  const { personSlug = '', variantKey } = useParams<{ personSlug: string; variantKey?: string }>();
  const [person, setPerson] = useState<PersonPublic | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'not-found' | 'error'>('loading');
  // Native-first: the picker probes the engine's booking API and fires
  // onFallback on 404 (no active native type), which re-enables the HubSpot
  // embed below. This is the whole migration model — a link flips to native
  // when its booking_types row is activated, with no code change here.
  // The bare /book/:personSlug route has no bookingKey to probe with (native
  // types are keyed host+key), so it goes straight to the HubSpot default.
  //
  // The BARE /book/:personSlug route (no variant key) is the generic personal
  // booking page. It has no key to probe with at mount, so it starts neutral
  // and is upgraded to 'try' once the person payload names their default
  // booking type. That can't flash the wrong surface: `status` is 'loading'
  // until that same fetch resolves, so nothing below renders in the meantime.
  const [nativeMode, setNativeMode] = useState<'try' | 'fallback' | 'pending'>(
    variantKey ? 'try' : 'pending',
  );
  // Funnel branding, resolved server-side from the booking type's brand and
  // handed up by the picker. Null until it arrives (or when falling back to
  // HubSpot) — the page renders neutral slate in the meantime, exactly as the
  // pre-brand version did, so non-native pages are pixel-identical.
  const [brand, setBrand] = useState<BookingBrandPayload | null>(null);
  const embedContainerRef = useRef<HTMLDivElement | null>(null);

  // Contact prefill — read once from URL params (fn/ln/em injected by emails or referral links).
  const prefill = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      firstName: p.get('fn') ?? '',
      lastName:  p.get('ln') ?? '',
      email:     p.get('em') ?? '',
    };
  }, []);

  useEffect(() => {
    if (!personSlug) { setStatus('not-found'); return; }
    let cancelled = false;
    (async () => {
      try {
        // First: when a variantKey is present, check whether a program owns this
        // discovery-call config. If so, redirect to the branded /lets-talk/<slug>
        // landing — that surface is the rebuilt page (host bio + headline +
        // expectations + trust quote + branded Calendly). The bare layout below
        // remains as the fallback for variants without a program binding.
        if (variantKey) {
          const lookup = await fetch(
            `${API}/api/public/discovery-call-program?host=${encodeURIComponent(personSlug)}&bookingKey=${encodeURIComponent(variantKey)}`,
          );
          if (lookup.ok) {
            const { programSlug } = await lookup.json() as { programSlug: string };
            if (programSlug && !cancelled) {
              window.location.replace(`/lets-talk/${encodeURIComponent(programSlug)}`);
              return; // leave status='loading' — the spinner shows until the redirect lands
            }
          }
        }

        const res = await fetchWithRetry(`${API}/api/public/people/${encodeURIComponent(personSlug)}`);
        // A transient 5xx isn't "this person doesn't exist" — only a real 404 is.
        // Mislabeling it not-found used to send a visitor straight to a dead-end
        // 404 page instead of a retryable error state.
        if (!res.ok) { if (!cancelled) setStatus(res.status === 404 ? 'not-found' : 'error'); return; }
        const data = await res.json() as { person: PersonPublic };
        if (cancelled) return;
        setPerson(data.person);
        // Bare route: a live default booking type turns the native picker on.
        // No default (or it's still inactive) means this person has no bookable
        // page, and 'fallback' hands the decision to the dead-end check below.
        if (!variantKey) setNativeMode(data.person.defaultBookingKey ? 'try' : 'fallback');
        setStatus('ready');
      } catch {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [personSlug, variantKey]);

  // Prefer named variant, fall back to default booking_url. (HubSpot side —
  // unrelated to the native key below.)
  const bookingUrl = person
    ? ((variantKey ? person.bookingUrls[variantKey] : null) ?? person.booking_url)
    : null;

  /** The booking_key the native picker probes: the URL's variant when there is
   *  one, otherwise this person's default generic type. */
  const nativeKey = variantKey ?? person?.defaultBookingKey ?? null;

  useEffect(() => {
    if (person) {
      const firstName = person.first_name ?? person.name.split(' ')[0];
      document.title = `Book a call with ${firstName} — ${brand?.name ?? BRAND_NAME}`;
    }
    return () => { document.title = BRAND_NAME; };
  }, [person, brand]);

  // Brand fonts (e.g. Poppins for BKOS) — one link tag, removed on unmount.
  useEffect(() => {
    if (!brand?.googleFontsUrl) return;
    const LINK_ID = 'booking-brand-fonts';
    if (document.getElementById(LINK_ID)) return;
    const link = document.createElement('link');
    link.id = LINK_ID;
    link.rel = 'stylesheet';
    link.href = brand.googleFontsUrl;
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, [brand?.googleFontsUrl]);

  // HubSpot meetings embed. MeetingsEmbedCode.js scans the DOM for
  // .meetings-iframe-container[data-src] and converts them to iframes.
  // Remove + re-add the script on each bookingUrl change so the scanner
  // re-runs after React has updated the container div.
  useEffect(() => {
    const container = embedContainerRef.current;
    // While the native picker owns the surface, the container div isn't
    // mounted; nativeMode is a dep so this re-runs the moment fallback flips.
    if (nativeMode !== 'fallback' || !bookingUrl || !container) return;

    while (container.firstChild) container.removeChild(container.firstChild);

    const base = bookingUrl.includes('?')
      ? `${bookingUrl}&embed=true`
      : `${bookingUrl}?embed=true`;

    const prefillParts: string[] = [];
    if (prefill.firstName) prefillParts.push(`firstName=${encodeURIComponent(prefill.firstName)}`);
    if (prefill.lastName)  prefillParts.push(`lastName=${encodeURIComponent(prefill.lastName)}`);
    if (prefill.email)     prefillParts.push(`email=${encodeURIComponent(prefill.email)}`);
    const embedSrc = prefillParts.length ? `${base}&${prefillParts.join('&')}` : base;

    const meetingsDiv = document.createElement('div');
    meetingsDiv.className = 'meetings-iframe-container';
    meetingsDiv.setAttribute('data-src', embedSrc);
    meetingsDiv.style.minHeight = '660px';
    container.appendChild(meetingsDiv);

    const SCRIPT_ID = 'hs-meetings-embed';
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.type = 'text/javascript';
    script.src = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
    document.head.appendChild(script);

    return () => {
      while (container.firstChild) container.removeChild(container.firstChild);
    };
  }, [nativeMode, bookingUrl, prefill]);

  if (status === 'loading') {
    return (
      <main style={pageStyle}>
        <p style={loadingStyle}>Loading…</p>
      </main>
    );
  }

  if (status === 'error') return <BrokenPage variant="error" />;

  // A missing HubSpot URL is only a dead end once the native probe has ALSO
  // fallen back — a native-only host legitimately has no booking_url at all.
  if (status === 'not-found' || (status === 'ready' && !bookingUrl && nativeMode === 'fallback')) {
    const noLinkMessage = status === 'ready' && !bookingUrl
      ? "There's no booking link available for this page yet. Tell us who you were trying to book with and we'll sort it."
      : undefined;
    return <BrokenPage variant="404" message={noLinkMessage} />;
  }

  // status === 'ready' && person && (native picker active || bookingUrl)
  // Brand vars cascade from the page root: the picker's styles already read
  // --prog-primary / --prog-tint / --prog-deep / --radius-button, so setting
  // them here restyles the whole booking surface with no picker changes.
  const pal = brand?.palette ?? null;
  const brandVars = pal
    ? ({
        '--prog-primary': pal.cta ?? pal.primary,
        '--prog-deep': pal.deep ?? pal.textDark,
        '--prog-tint': pal.tint,
        ...(brand?.designTokens?.radius?.button
          ? { '--radius-button': `${brand.designTokens.radius.button}px` }
          : {}),
      } as React.CSSProperties)
    : {};
  const brandFont = brand?.fonts?.body?.family
    ? { fontFamily: `'${brand.fonts.body.family}', 'Inter', -apple-system, sans-serif` }
    : {};
  return (
    <main style={{ ...pageStyle, ...brandVars, ...brandFont, ...(pal?.canvas ? { background: pal.canvas } : {}) }}>
      <TopBar logoUrl={brand?.logoUrl ?? null} logoLinkUrl={brand?.logoLinkUrl ?? null} brandName={brand?.name ?? null} />
      <div style={shellStyle}>
        <div style={layoutStyle}>
          <AboutPanel person={person!} />

          <div style={embedWrapStyle}>
            {nativeMode === 'try' && nativeKey ? (
              <NativeBookingPicker
                personSlug={personSlug}
                bookingKey={nativeKey}
                prefill={prefill}
                onFallback={() => { setNativeMode('fallback'); setBrand(null); }}
                onBrand={setBrand}
              />
            ) : (
              <div ref={embedContainerRef} style={{ width: '100%' }} />
            )}
          </div>
        </div>
      </div>
      <footer style={footerStyle}>
        <a href={BRAND_HOME_URL} style={footLinkStyle}>{BRAND_NAME}</a>
        {' · '}
        <a href={`mailto:${SUPPORT_EMAIL}`} style={footLinkStyle}>{SUPPORT_EMAIL}</a>
        <div style={{ marginTop: 8 }}><PolicyLinks /></div>
      </footer>
    </main>
  );
}

/**
 * "A bit about me, and where else to find me" — the left-hand panel of a
 * booking page.
 *
 * It is the whole difference between a scheduling widget and a page worth
 * landing on, which is what a personal booking link needs to be when someone
 * chooses to put it in their signature (Michael, 2026-09-19).
 *
 * Every field degrades to nothing. A teammate CC has only a name for renders a
 * name and the picker, never an empty heading over a blank links row — which
 * matters because a page is provisioned for every staff member automatically,
 * including ones whose profile CC has barely filled in yet.
 */
function AboutPanel({ person }: { person: PersonPublic }) {
  // bio_long when CC has one, else the short bio. Today CC populates only
  // `bio` for everyone, so this is forward-looking rather than decorative:
  // the long form is the one a booking page wants, and the moment CC fills it
  // in the page picks it up without a deploy.
  const bio = person.bio_long ?? person.bio;

  // CC's mirrored social map is the source of truth when it has anything. The
  // older EE-owned linkedin_url / instagram_url columns stay as the fallback —
  // local-only people (never in CC's staff feed) still rely on them, and
  // dropping them would silently blank those pages.
  const links = [
    person.teamPageUrl ? { label: 'Profile', url: person.teamPageUrl } : null,
    person.websiteUrl ? { label: 'Website', url: person.websiteUrl } : null,
    ...(person.social?.length
      ? person.social.map((s) => ({ label: socialLabel(s.network), url: s.url }))
      : [
          person.linkedin_url ? { label: 'LinkedIn', url: person.linkedin_url } : null,
          person.instagram_url ? { label: 'Instagram', url: person.instagram_url } : null,
        ]),
  ].filter((l): l is { label: string; url: string } => l !== null);

  return (
    <aside style={personCardStyle}>
      {person.photoUrl && <img src={person.photoUrl} alt={person.name} style={photoStyle} />}
      <h1 style={personNameStyle}>{person.name}</h1>
      {/* whiteSpace:'pre-line' is load-bearing, not styling: CC stores some
          titles as two lines (Michael's is "Founder\nHost, The Successful
          Bookkeeper Podcast") and HTML would otherwise collapse the break into
          a space and run the two roles together. */}
      {person.role && <p style={personRoleStyle}>{person.role}</p>}
      {bio && <p style={personBioStyle}>{bio}</p>}
      {links.length > 0 && (
        <div style={linksWrapStyle}>
          {links.map((l) => (
            <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" style={linkChipStyle}>
              {l.label}
            </a>
          ))}
        </div>
      )}
    </aside>
  );
}

function TopBar({ logoUrl, logoLinkUrl, brandName }: { logoUrl: string | null; logoLinkUrl: string | null; brandName: string | null }) {
  return (
    <div style={topBarStyle}>
      {logoUrl ? (
        <a href={logoLinkUrl ?? BRAND_HOME_URL}>
          <img src={logoUrl} alt={brandName ?? BRAND_NAME} style={{ height: 26, display: 'block' }} />
        </a>
      ) : (
        <a href={BRAND_HOME_URL} style={wordmarkStyle}>{brandName ?? BRAND_NAME}</a>
      )}
    </div>
  );
}

// ---- styles ----

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#F8FAFC',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  display: 'flex',
  flexDirection: 'column',
};

const topBarStyle: React.CSSProperties = {
  background: '#ffffff',
  borderBottom: '1px solid #E2E8F0',
  padding: '14px 24px',
};

const wordmarkStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: '#0F172A',
  textDecoration: 'none',
  letterSpacing: '-0.01em',
};

const shellStyle: React.CSSProperties = {
  maxWidth: 1100,
  margin: '0 auto',
  padding: '40px 20px',
  width: '100%',
  flex: 1,
};

const layoutStyle: React.CSSProperties = {
  display: 'flex',
  gap: 32,
  alignItems: 'flex-start',
  flexWrap: 'wrap',
};

const personCardStyle: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: 16,
  padding: '32px 24px',
  width: 280,
  flexShrink: 0,
  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
  textAlign: 'center',
};

const photoStyle: React.CSSProperties = {
  width: 96,
  height: 96,
  borderRadius: '50%',
  objectFit: 'cover',
  display: 'block',
  margin: '0 auto 16px',
};

const personNameStyle: React.CSSProperties = {
  margin: '0 0 4px',
  fontSize: 18,
  fontWeight: 700,
  color: '#0F172A',
  letterSpacing: '-0.01em',
};

const personRoleStyle: React.CSSProperties = {
  margin: '0 0 14px',
  fontSize: 13,
  color: '#64748B',
  fontWeight: 500,
  whiteSpace: 'pre-line',
  lineHeight: 1.45,
};

const personBioStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 13.5,
  lineHeight: 1.6,
  color: '#475569',
  textAlign: 'left',
};

const linksWrapStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  justifyContent: 'flex-start',
  marginTop: 16,
  paddingTop: 16,
  borderTop: '1px solid #F1F5F9',
};

const linkChipStyle: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  color: '#475569',
  textDecoration: 'none',
  padding: '5px 10px',
  borderRadius: 999,
  border: '1px solid #E2E8F0',
  background: '#F8FAFC',
  lineHeight: 1.2,
};

const embedWrapStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 320,
  background: '#ffffff',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)',
  minHeight: 660,
};

const loadingStyle: React.CSSProperties = {
  display: 'flex' as const,
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  color: '#94A3B8',
  fontSize: 15,
  margin: 0,
};

const footerStyle: React.CSSProperties = {
  padding: '16px 24px',
  textAlign: 'center',
  fontSize: 12,
  color: '#94A3B8',
  borderTop: '1px solid #F1F5F9',
};

const footLinkStyle: React.CSSProperties = {
  color: '#94A3B8',
  textDecoration: 'none',
};
